import { failingFetcher } from '@lib/http/fetch';
import { GetProductListsV2Response } from '@lib/types/api/ProductListV2';
import {
  GetPublisherOrganizationResponse,
  PublisherOrganization,
} from '@lib/types/api/PublisherOrganization';
import { useMemo } from 'react';
import useSWR from 'swr';

export type ListsData = {
  personalLists: GetProductListsV2Response;
  orgLists: GetProductListsV2Response;
  orgListIds: Set<string>;
  publisherOrganization: PublisherOrganization | null;
  organizationId: string | undefined;
  isOrgSettled: boolean;
  isLoading: boolean;
  mutatePersonalLists: () => Promise<unknown>;
  mutateOrgLists: () => Promise<unknown>;
};

export function useListsData(shouldFetch: boolean): ListsData {
  // The wishlist's organization is the publisher organization, resolved
  // server-side and independent of the active organization (CPS-7561).
  const { data: publisherOrgData, error: publisherOrgError } =
    useSWR<GetPublisherOrganizationResponse>(
      shouldFetch ? '/api/users/publisher-organization' : null,
      failingFetcher
    );

  const publisherOrganization = publisherOrgData?.publisherOrganization ?? null;
  const organizationId = publisherOrganization?.id;

  const {
    data: personalData,
    error: personalError,
    mutate: mutatePersonalLists,
  } = useSWR<GetProductListsV2Response>(
    shouldFetch ? '/api/lists' : null,
    failingFetcher
  );

  const {
    data: orgListsData,
    error: orgListsError,
    mutate: mutateOrgLists,
  } = useSWR<GetProductListsV2Response>(
    shouldFetch && organizationId
      ? `/api/organizations/${organizationId}/lists`
      : null,
    failingFetcher
  );

  const personalLists = useMemo(() => personalData ?? [], [personalData]);
  const orgLists = useMemo(() => orgListsData ?? [], [orgListsData]);
  const orgListIds = useMemo(
    () => new Set(orgLists.map((list) => list.id)),
    [orgLists]
  );

  return {
    personalLists,
    orgLists,
    orgListIds,
    publisherOrganization,
    organizationId,
    // The membership lookup waits for this so its first request carries
    // `org-id`. An error counts as settled — `shouldRetryOnError: false` makes a
    // failed resolution terminal, and waiting on data alone would leave every
    // bookmark loading forever.
    isOrgSettled: !shouldFetch || !!publisherOrgData || !!publisherOrgError,
    // Every leg treats an error as settled, for the same reason as
    // `isOrgSettled`: `shouldRetryOnError: false` makes a failure terminal, so
    // waiting on data alone would pin `isLoading` true for the whole session.
    isLoading:
      shouldFetch &&
      ((!personalData && !personalError) ||
        (!publisherOrgData && !publisherOrgError) ||
        (!!organizationId && !orgListsData && !orgListsError)),
    mutatePersonalLists,
    mutateOrgLists,
  };
}
